<template>
  <template v-if="text || $slots.default">
    <template v-if="hasHtml">
      <!-- eslint-disable vue/no-v-html -->
      <h1
        v-if="tag === 'h1'"
        :id="$attrs.id"
        :class="classes"
        v-html="cleanedText"
      />
      <h2
        v-else-if="tag === 'h2'"
        :id="$attrs.id"
        :class="classes"
        v-html="cleanedText"
      />
      <h3
        v-else-if="tag === 'h3'"
        :id="$attrs.id"
        :class="classes"
        v-html="cleanedText"
      />
      <h4
        v-else-if="tag === 'h4'"
        :id="$attrs.id"
        :class="classes"
        v-html="cleanedText"
      />
      <h5
        v-else-if="tag === 'h5'"
        :id="$attrs.id"
        :class="classes"
        v-html="cleanedText"
      />
      <h6
        v-else-if="tag === 'h6'"
        :id="$attrs.id"
        :class="classes"
        v-html="cleanedText"
      />
    </template>
    <component
      :is="tag"
      v-else
      :id="$attrs.id"
      :class="classes"
    >
      <slot>{{ cleanedText }}</slot>
    </component>
  </template>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue';
import { decodeHTML } from 'entities';
const attrs = useAttrs();
const props = defineProps({
  text: {
    type: String,
    required: false,
    default: null,
    validator: (value: string) => value.length > 0
  },
  tag: {
    type: String,
    required: false,
    default: 'h3',
    validator: (value: string) => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(value)
  }
});
const classes = computed(() => [props.tag === 'h1' ? 'dn-headline' : 'dn-headline--subhead', attrs.class]);
const cleanedText = computed(() => {
  const allowedTags = /^(br|b|i|strong|em)$/;
  const tagRegex = /<\/?(\w+)[^>]*>/g;

  return props.text
    ? decodeHTML(
      props.text.replace(tagRegex, function (tag) {
        const tagNameMatch = tag.match(/<\/?(\w+)[^>]*>/);
        const tagName = tagNameMatch ? tagNameMatch[1] : null;
        if (tagName && allowedTags.test(tagName.toLowerCase())) {
          return tag;
        } else {
          return '';
        }
      })
    )
    : '';
});

const hasHtml = computed(() => {
  const regex = /<[^>]*>/; // This regex checks for HTML tags

  return regex.test(cleanedText.value);
});
</script>

<style scoped>
[class*='dn-headline'] {
  --dn_color: var(--dn-headline_color);
  --dn_font-family: var(--dn-headline_font-family);
  --dn_font-size: var(--dn-headline_font-size);
  --dn_font-weight: var(--dn-headline_font-weight);
  --dn_letter-spacing: var(--dn-headline_letter-spacing);
  --dn_line-height: var(--dn-headline_line-height);
  --dn_text-align: var(--dn-headline_text-align);
  --dn_text-transform: var(--dn-headline_text-transform);
  --_dn-headline_spacing: var(--dn-headline_spacing);
  --_dn-headline_border-bottom-width: var(--dn-headline_border-bottom-width);
  --_dn-headline_border-bottom-color: var(--dn-headline_border-bottom-color);
  --_dn-headline_text-wrap: var(--dn-headline_text-wrap, normal);
  --_dn-headline_white-space: var(--dn-headline_white-space);

  text-wrap: var(--_dn-headline_text-wrap);
  font-family: var(--dn_font-family);
  font-size: var(--dn_font-size);
  border-bottom: var(--_dn-headline_border-bottom-width) solid var(--_dn-headline_border-bottom-color);
  padding: var(--_dn-headline_spacing);
  white-space: var(--_dn-headline_white-space);
}
</style>
